/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

/** UPDATED 12/09/2025 */

import React, { useState } from "react";
import axios from "../api/axiosInstance";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import enGB from "date-fns/locale/en-GB";
import ca from "date-fns/locale/ca";
import { usePmsTheme } from "./PmsThemeContext";
import { createReservationTranslator, getReservationLocale, translateReservationStatus } from "../reservations/reservationI18n";
import { reservationFormMessages } from "../reservations/reservationFormMessages";
import "react-datepicker/dist/react-datepicker.css";
import OverridePopup from './OverridePopup';
import { refetchReservationAfterReset } from "../utils/refetchers";
import ReservationExtrasModal from "./ReservationExtrasModal";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import "./ReservationForm.css";



registerLocale("es", es);
registerLocale("en-GB", enGB);
registerLocale("ca", ca);

const firstRoomMetaValue = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "-";
};

const getRoomMetadataLines = (room) => [
  {
    label: "Floor",
    value: firstRoomMetaValue(room?.floor, room?.room_floor),
  },
  {
    label: "Bed type",
    value: firstRoomMetaValue(room?.bed_type, room?.bedType, room?.room_bed_type),
  },
  {
    label: "View",
    value: firstRoomMetaValue(room?.view, room?.views, room?.room_view),
  },
];


const ReservationForm = ({
  handleSubmit,
  formData,
  handleChange,
  minCheckInDate,
  isResettingRef,
  handleAutoSave,
  dbDatesMap,
  selectedReservationId,
  setIsEditingDates,
  isEditingDates,
  tempRange,
  setTempRange,
  setFormData,
  adultsMap,
  setIsEditingAdults,
  isEditingAdults,
  availableAdults,
  setAdultsMap,
  setChildrenMap,
  setRoomCategoryMap,
  setAvailableAdults,
  setRoomIdMap,
  setRoomRateDetailsMap,
  handleResetCapacityOnly,
  handleResetChildrenCapacityOnly,
  handleResetRoomCategoryOnly,
  isEditingChildren,
  setIsEditingChildren,
  setAvailableChildren,
  availableChildren,
  childrenMap,
  visibleRoomCategories,
  isEditingRoomCategory,
  setIsEditingRoomCategory,
  roomCategoryDetailsMap,
  rooms: roomsProp,
  isEditingRoom,
  setIsEditingRoom,
  roomIdMap,
  roomRates,
  setRoomRates,
  isEditingRate,
  setIsEditingRate,
  roomRateDetailsMap,
  groupReservations,
  reservationIdRef,
  formatDate,
  showCopyPanel,
  handleResetDates,
  DateRange,
  reloadDatesFromDB,
  setDbDatesMap,
  handleDeleteReservation,
  hasAnyIncompleteReservation,
  fetchCopyInfo,
  
}) => {
  const { languageId } = usePmsTheme();
  const t = React.useMemo(() => createReservationTranslator(languageId, reservationFormMessages), [languageId]);
  const datePickerLocale = ({ es: "es", ca: "ca", en: "en-GB" })[languageId] || "en-GB";
  const datePickerLabels = {
    previousMonthButtonLabel: t("Previous Month"),
    previousMonthAriaLabel: t("Previous Month"),
    nextMonthButtonLabel: t("Next Month"),
    nextMonthAriaLabel: t("Next Month"),
    monthAriaLabelPrefix: t("Month"),
    chooseDayAriaLabelPrefix: t("Choose"),
    disabledDayAriaLabelPrefix: t("Not available"),
  };
  const { baseCurrency, displayCurrency, formatFromBase } = useCurrencyDisplay();
  // Aquí puedes definir funciones auxiliares propias del form si hace falta
  // (por ejemplo, formatDate)
  // Si ya las tienes definidas, cópialas aquí también
  // No toques los imports de librerías de UI todavía

   // 🔐 Load current user from localStorage to check permissions
  const currentUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("palantir_user")) || null;
    } catch {
      return null;
    }
  }, []);

  // DB is the source of truth → local mirror to render the button safely
  const [, setHasOverrideFlag] = React.useState(
    typeof formData?.has_override === "boolean" ? formData.has_override : null
  );

  // Prefer reservation id from formData; fallback to selectedReservationId if exists in scope
  const effectiveReservationId = formData?.id ?? (typeof selectedReservationId !== "undefined" ? selectedReservationId : null);

  React.useEffect(() => {
    let cancelled = false;

    async function syncHasOverride() {
      try {
        // If we already have a boolean from formData, trust it; else query API.
        if (typeof formData?.has_override === "boolean") {
          if (!cancelled) setHasOverrideFlag(formData.has_override);
          console.log("[Overrides][UI][Sync] using formData.has_override =", formData.has_override);
          return;
        }

        if (!effectiveReservationId) {
          console.warn("[Overrides][UI][Sync] No reservation id available to fetch has_override");
          if (!cancelled) setHasOverrideFlag(false);
          return;
        }

        console.log("[Overrides][UI][Sync] fetching reservation to get has_override → id:", effectiveReservationId);
        const { data } = await axios.get(`/api/reservations/${effectiveReservationId}`);
        const val = Boolean(data?.has_override);
        if (!cancelled) setHasOverrideFlag(val);
        console.log("[Overrides][UI][Sync] fetched has_override =", val);
      } catch (err) {
        console.error("[Overrides][UI][Sync] failed to fetch has_override:", err?.message);
        if (!cancelled) setHasOverrideFlag(false);
      }
    }

    syncHasOverride();
    return () => { cancelled = true; };
  // Re-sync if the reservation changes or formData updates
  }, [effectiveReservationId, formData?.has_override]);

 
  console.log("RESERVATION FORM DATA:", formData);

  const isGroupRate = !!formData.group_rate_id;

  const [isOverridePopupOpen, setIsOverridePopupOpen] = React.useState(false);

  

  const [, setCustomerData] = React.useState(null);
  const [, setGuestCustomers] = React.useState([]);
  const [, setGuestChildren] = React.useState([]);
  
  

  // ⬇️ estado del modal + datos del desglose
  const [showPriceModal, setShowPriceModal] = React.useState(false);
  const [nightly, setNightly] = React.useState([]);
  const [loadingBreakdown, setLoadingBreakdown] = React.useState(false);
  const [breakdownError, setBreakdownError] = React.useState(null);

  const canLoadBreakdown = Boolean(
    formData?.rate_id && formData?.check_in_date && formData?.check_out_date
  );

  const toHHMM = (t) => (t ? String(t).slice(0, 5) : "");
  const toDbTime = (hhmm) => {
    const v = (hhmm || "").trim();
    if (!v) return null;
    return v.length === 5 ? `${v}:00` : v; // "HH:MM" -> "HH:MM:SS"
  };

  const openPriceModal = async () => {
    setShowPriceModal(true);

    // ⚠️ Si no hay todavía reserva guardada, no podemos traer nightlyPrices
    if (!selectedReservationId || selectedReservationId === "summary") {
      setBreakdownError("This reservation does not have a saved breakdown yet.");
      return;
    }

    setLoadingBreakdown(true);
    setBreakdownError(null);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservations/${selectedReservationId}/nightly-prices`
      );

      const rows = Array.isArray(data)
        ? data.map(r => ({
            date: r.date,
            price: Number(r.price),
            rate_price_id: r.rate_price_id,
            room_rate_id: r.room_rate_id,
          }))
        : [];

      setNightly(rows);
    } catch (err) {
      console.error("[ReservationForm][NightlyBreakdown] Failed to load breakdown:", err);
      setBreakdownError(
        "The nightly breakdown could not be loaded."
      );
    } finally {
      setLoadingBreakdown(false);
    }
  };


  const closePriceModal = () => setShowPriceModal(false);

  // 🔹 Estado para abrir/cerrar el modal de extras
  const [extrasModalMode, setExtrasModalMode] = useState(null); 
  // values: "details" | "add" | "remove" | null

  const [roomsState, setRoomsState] = useState(roomsProp || []);
  const [availableRoomCategories, setAvailableRoomCategories] = useState([]);

  const rooms = roomsState; // 👌 ahora sí, no hay duplicado

  // 🟣 Control de bloqueo total al guardar
  const [isSaving, setIsSaving] = useState(false);






  return (
  selectedReservationId !== "summary" && (
    <div className="pilcoreReservationForm">
      {console.log("[Apply][ReservationForm][Render]", { selectedReservationId, isSummary: selectedReservationId === "summary" })}

      <form 
        className="reservationFormShell"
        onSubmit={(e) => {
          setIsSaving(true); // bloquea toda la pantalla inmediatamente
          handleSubmit(e);   // llama al original que ya hace navigate()
          setTimeout(() => setIsSaving(false), 5000); // desbloquea por si no hay redirección
        }}
        style={{ display: "flex", flexDirection: "column", width: "100%" }}
      >

          
          {/* 🟣 COLUMNA IZQUIERDA: Formulario de reserva */}
          <div
            style={{
              flex: selectedReservationId === "summary" ? "0 0 100%" : 1,
              maxWidth: selectedReservationId === "summary" ? "100%" : "unset"
            }}
          >
    
    
            <div
              className="card p-4 reservationFormCard"
              style={{
                fontSize: "0.85rem",
                width: "100%",
                marginLeft: "0",
                marginRight: "auto",
                filter: showCopyPanel ? "blur(2px)" : "none",
                transition: "filter 0.2s ease-in-out"
              }}
            >
             
    
              {/* Aquí siguen los <div className="row"> y todos los campos del formulario */}
    
            <div className="row reservationFormGrid">
              <div className="col-md-2 mb-3" style={{ display: "none" }}> {/* Si tiene display none es porque no se utiliza, pero quitarlo desmontaba otras partes del código y por eso se dejó. */}
                <label className="form-label">{t("Check-in Date")}</label>
                <input 
                  type="date" 
                  name="check_in_date" 
                  value={formData.check_in_date} 
                  onChange={handleChange} 
                  disabled={!!formData.check_in_date && !!formData.check_out_date}
                  onBlur={(e) => {
                    if (window.__isResettingDates || isResettingRef.current) {
                      console.log("⏳ Ignorando onBlur de check_in_date porque estamos reseteando");
                      return;
                    }
                  
    
                    const value = e.target.value;
                    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                      const selectedDate = new Date(value);
                      const today = new Date();
                      const yesterday = new Date(today);
                      yesterday.setDate(today.getDate() - 1);
                      yesterday.setHours(0, 0, 0, 0);
    
                      if (selectedDate < yesterday) {
                        alert(t("check-In date cannot be before yesterday."));
                        e.target.value = "";
                        handleChange({ target: { name: "check_in_date", value: "" } });
                        return;
                      }
    
                      handleAutoSave("check_in_date", value).then(async () => {
                        
                      });
    
                    }
                  }}
    
                  required 
                  min={minCheckInDate}
                  className="form-control form-control-sm p-1" 
                />      
              </div>
    
    
    
    
    
    
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("Check-In → Check-Out")}</label>

                <div
                  className="form-control form-control-sm bg-light text-dark reservationPickerTrigger"
                  style={{
                    cursor: isGroupRate ? "not-allowed" : "pointer",
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    minHeight: "42px", // 👈 recupero tu altura
                    display: "flex",
                    alignItems: "center",
                    opacity: isGroupRate ? 0.6 : 1
                  }}
                  onClick={async () => {
                    if (isGroupRate) {
                      alert(t("Reservations with Group Rate cannot be modified. Please delete and create a new reservation."));
                      return;
                    }

                    // 🚩 SECURITY FILTER: Don't allow editing if another reservation is "in progress"
                    const incomplete = groupReservations.find(
                      r =>
                        r.id !== selectedReservationId &&
                        ((r.check_in_date && r.check_out_date) && (!r.rate_id || r.rate_id === ""))
                    );
                    if (incomplete) {
                      const roomIndex = groupReservations.findIndex(r => r.id === incomplete.id) + 1;
                      alert(
                        t("You can only edit reservations for one room at a time.\n\nPlease complete the other reservation (Room {roomIndex}) by selecting a rate before continuing.", { roomIndex })
                      );
                      return;
                    }

                    const reservationId = selectedReservationId;

                    if (!reservationId) {
                      console.error("🚨 Abort PATCH: reservationId is null");
                      return;
                    }
                    setIsEditingDates(true);
                  }}
                >
                  <span
                    style={
                      dbDatesMap[selectedReservationId]?.check_in_date && dbDatesMap[selectedReservationId]?.check_out_date
                        ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" }     // seleccionado: más legible
                        : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }   // vacío: pequeño y muted
                    }
                  >
                    {dbDatesMap[selectedReservationId]?.check_in_date && dbDatesMap[selectedReservationId]?.check_out_date
                      ? `${formatDate(dbDatesMap[selectedReservationId].check_in_date)} → ${formatDate(dbDatesMap[selectedReservationId].check_out_date)}`
                      : t("Click to Select Dates")}
                  </span>
                </div>
              </div>


    
    
              {isEditingDates && (
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{
                    zIndex: 1050,
                    backdropFilter: "blur(6px)",
                    backgroundColor: "rgba(23, 38, 83, 0.42)"
                  }}
                >
                  <div
                    className="rounded p-4 shadow-lg"
                    style={{
                      minWidth: 380,
                      backgroundColor: "#ffffff",
                      color: "#26354f"
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="m-0">{t("Edit Dates")}</h5>
                      <button
                        type="button"
                        className="btn-close" aria-label={t("Close")}
                        onClick={() => setIsEditingDates(false)}
                      ></button>
                    </div>
    
                    {/* 📆 Calendario con rango */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: "1rem", marginBottom: "0.7rem" }}>
                        <DatePicker
                          {...datePickerLabels}
                          selected={tempRange.startDate}
                          onChange={date => setTempRange({ ...tempRange, startDate: date })}
                          selectsStart
                          startDate={tempRange.startDate}
                          endDate={tempRange.endDate}
                          minDate={new Date()}
                          maxDate={tempRange.endDate}
                          locale={datePickerLocale}
                          dateFormat="dd/MM/yyyy"
                          placeholderText={t("Check In")}
                          customInput={<input className="fashion-input" />}
                          open={false}
                        />
                        <DatePicker
                          {...datePickerLabels}
                          selected={tempRange.endDate}
                          onChange={date => setTempRange({ ...tempRange, endDate: date })}
                          selectsEnd
                          startDate={tempRange.startDate}
                          endDate={tempRange.endDate}
                          minDate={tempRange.startDate || new Date()}
                          locale={datePickerLocale}
                          dateFormat="dd/MM/yyyy"
                          placeholderText={t("Check Out")}
                          customInput={<input className="fashion-input" />}
                          open={false}
                        />
                      </div>

                      <DatePicker
                        {...datePickerLabels}
                        selected={tempRange.startDate}
                        onChange={dates => {
                          const [start, end] = dates;
                          setTempRange({ startDate: start, endDate: end });
                        }}
                        startDate={tempRange.startDate}
                        endDate={tempRange.endDate}
                        selectsRange
                        minDate={new Date()}
                        monthsShown={2}
                        locale={datePickerLocale}
                        dateFormat="dd/MM/yyyy"
                        calendarClassName="custom-datepicker-dark"
                        dayClassName={() => "custom-datepicker-dark-day"}
                        inline
                      />
                    </div>

             
    
                    {/* ✅ Botones */}
                    <div className="d-flex justify-content-end mt-4 gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setIsEditingDates(false);
                          setTempRange({ startDate: null, endDate: null });
                        }}
                      >
                        {t("Cancel")}
                      </button>
    
                      <button
                        type="button"
                        className="btn btn-warning btn-sm fw-bold"
                        onClick={async () => {
                          const checkIn = tempRange.startDate;
                          const checkOut = tempRange.endDate;

                          if (!checkIn || !checkOut) {
                            alert(t("Please select both dates."));
                            return;
                          }

                          if (checkOut <= checkIn) {
                            alert(t("Check-out must be after check-in."));
                            return;
                          }

                          try {
                            const reservationId = selectedReservationId;

                            if (!reservationId) {
                              console.error("🚨 Abort PATCH: reservationId is null");
                              return;
                            }

                            const checkInStr = `${checkIn.getFullYear()}-${String(checkIn.getMonth() + 1).padStart(2, "0")}-${String(checkIn.getDate()).padStart(2, "0")}`;
                            const checkOutStr = `${checkOut.getFullYear()}-${String(checkOut.getMonth() + 1).padStart(2, "0")}-${String(checkOut.getDate()).padStart(2, "0")}`;

                            // 🔥 0️⃣ SI HAY OVERRIDES → BORRARLOS ANTES DE CAMBIAR FECHAS
                            if (formData.has_override) {
                              await axios.delete(
                                `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}/overrides`
                              );
                            }

                            // 🔥 1️⃣ PATCH FECHAS (BD = fuente de verdad)
                            await axios.patch(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`,
                              {
                                check_in_date: checkInStr,
                                check_out_date: checkOutStr,
                                updated_by: "ManualDatesEdit"
                              }
                            );

                            // 🔥 2️⃣ CALCULAR NOCHES
                            const diff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                            await axios.patch(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`,
                              {
                                room_nights: diff,
                                updated_by: "RoomNightsCalc"
                              }
                            );

                            // 🔁 3️⃣ RELEER DRAFT (solo para IDs necesarios)
                            const { data } = await axios.get(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`
                            );

                            // 🔥 4️⃣ RESET FRONT PREVIO
                            setFormData({ ...data });

                            // 🔥 5️⃣ LIMPIEZA EXPLÍCITA DE ESTADOS DERIVADOS
                            setAdultsMap(prev => ({ ...prev, [reservationId]: null }));
                            setChildrenMap(prev => ({ ...prev, [reservationId]: null }));
                            setAvailableAdults([]);
                            setAvailableChildren([]);
                            setAvailableRoomCategories([]);
                            setRoomRates([]);
                            setRoomsState([]);

                            setIsEditingRoom(false);
                            setIsEditingRoomCategory(false);
                            setIsEditingRate(false);

                            // 🔥 6️⃣ POPULAR SNAPSHOT DE DISPONIBILIDAD (AQUÍ SE RESETEA TODO EN BD)
                            const populateRes = await axios.post(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-availability/populate`,
                              {
                                reservation_group_id: data.reservation_group_id,
                                reservation_draft_id: reservationId,
                                business_unit_id: data.business_unit_id,
                                start_date: checkInStr,
                                end_date: checkOutStr
                              }
                            );

                            if (populateRes.data?.has_availability !== true) {
                              alert(t("No availability for the selected dates."));

                              // 🔥 rollback FECHAS EN BD
                              await axios.patch(
                                `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`,
                                {
                                  check_in_date: null,
                                  check_out_date: null,
                                  room_nights: null,
                                  updated_by: "DatesNoAvailabilityRollback"
                                }
                              );

                              const { data: rolledBack } = await axios.get(
                                `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`
                              );

                              setFormData({ ...rolledBack });
                              await reloadDatesFromDB(reservationId, setDbDatesMap);

                              setIsEditingDates(false);
                              setTempRange({ startDate: null, endDate: null });
                              return;
                            }

                            // ✅ 🔁 7️⃣ RELEER DRAFT FINAL (FUENTE DE VERDAD REAL)
                            const { data: freshDraft } = await axios.get(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`
                            );

                            // ✅ 🔥 PINTAR SOLO LO QUE HAY EN BD
                            setFormData({ ...freshDraft });

                            // 🔁 8️⃣ RECARGAR FECHAS VISUALES
                            await reloadDatesFromDB(reservationId, setDbDatesMap);

                            setTempRange({ startDate: null, endDate: null });
                            setIsEditingDates(false);

                          } catch (err) {
                            console.error("❌ Error saving dates:", err);
                            alert(t("Error saving dates."));
                          }
                        }}

                      >
                        {t("Save")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
    
    
    
    
              
    
              <div className="col-md-2 mb-3" style={{ display: "none" }}>
                <label className="form-label">{t("Check-out Date")}</label>
                <input 
                  type="date" 
                  name="check_out_date" 
                  value={formData.check_out_date} 
                  onChange={handleChange}
                  disabled={!formData.check_in_date}
                  onBlur={(e) => {
                    if (window.__isResettingDates) {
                      console.log("⏳ Ignorando onBlur de check_out_date porque estamos reseteando");
                      return;
                    }
                  
                    const value = e.target.value;
                    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                      const checkIn = new Date(formData.check_in_date);
                      const checkOut = new Date(value);
                      if (checkOut <= checkIn) {
                        alert(t("Check-Out date cannot be same or before the Check-In date."));
                        e.target.value = "";
                        handleChange({ target: { name: "check_out_date", value: "" } });
                        return;
                      }
                      handleAutoSave("check_out_date", value);
                    }
                  }}
                  
                  min={formData.check_in_date}
                  required 
                  className="form-control form-control-sm p-1" 
                />
              </div>
    
    
    
            <div className="col-md-2 mb-3">
              <label className="form-label">{t("Nights")}</label>
              <input
                type="number"
                name="room_nights"
                value={formData.room_nights}
                readOnly
                className="form-control bg-secondary text-light fw-bold" // 👈 blanco + negrita
                style={{
                  minHeight: "42px",   // 👈 caja más alta
                  fontSize: "0.95rem", // 👈 texto más grande
                  lineHeight: "1.2"    // 👈 mantiene altura de línea equilibrada
                }}
              />
            </div>

    
              <div className="col-md-2 mb-3">
              <label className="form-label">{t("Adults")}</label>

              <div
                className="form-control form-control-sm bg-light text-dark reservationPickerTrigger"
                style={{
                  cursor: formData.group_rate_id
                    ? "not-allowed"
                    : formData.check_in_date && formData.check_out_date
                    ? "pointer"
                    : "default",
                  opacity: formData.group_rate_id
                    ? 0.6
                    : formData.check_in_date && formData.check_out_date
                    ? 1
                    : 0.6,
                  minHeight: "42px",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={async () => {
                  if (formData.group_rate_id) {
                    alert(
                      t("Reservations with Group Rate cannot be modified. Please delete and create a new reservation.")
                    );
                    return;
                  }

                  if (!formData.check_in_date || !formData.check_out_date) return;

                  try {
                    const res = await axios.get(
                      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}/valid-adults`
                    );

                    setAvailableAdults(res.data.valid_adults || []);
                    setIsEditingAdults(true);

                    console.log("[Adults][LOAD_OPTIONS]", {
                      reservation_draft_id: selectedReservationId,
                      options: res.data.valid_adults,
                    });
                  } catch (err) {
                    console.error("[Adults][ERROR_LOADING_OPTIONS]", err);
                    alert(t("Error loading adults options."));
                  }
                }}
              >
                <span
                  style={
                    adultsMap[selectedReservationId]
                      ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" }
                      : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                  }
                >
                  {adultsMap[selectedReservationId] || t("Select")}
                </span>
              </div>

              {isEditingAdults && (
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{
                    zIndex: 1050,
                    backdropFilter: "blur(6px)",
                    backgroundColor: "rgba(23, 38, 83, 0.42)",
                  }}
                >
                  <div
                    className="rounded p-4 shadow-lg"
                    style={{
                      minWidth: 250,
                      maxHeight: "80vh",
                      overflowY: "auto",
                      backgroundColor: "#ffffff",
                      color: "#26354f",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="m-0">{t("Select Adults")}</h5>
                      <button
                        type="button"
                        className="btn-close" aria-label={t("Close")}
                        onClick={() => setIsEditingAdults(false)}
                      />
                    </div>

                    {availableAdults.length === 0 && (
                      <div className="text-muted text-center">
                        {t("No options available")}
                      </div>
                    )}

                    {availableAdults.map((num) => (
                      <div
                        key={num}
                        className="mb-2 p-2 border rounded text-center"
                        style={{ cursor: "pointer", backgroundColor: "#ffffff" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#edf8fc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "#ffffff")
                        }
                        onClick={async () => {
                          try {
                            await axios.patch(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`,
                              {
                                adults: num,
                                updated_by: "SelectAdults",
                              }
                            );

                            // 🔥 volver a leer desde BD y pintar TODO
                            const { data } = await axios.get(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                            );

                            setFormData({
                              ...data
                            });

                            // 🔥 sincronizar mapas visuales con BD
                            setAdultsMap(prev => ({
                              ...prev,
                              [selectedReservationId]: data.adults
                            }));

                            setChildrenMap(prev => ({
                              ...prev,
                              [selectedReservationId]: data.children
                            }));

                            setIsEditingAdults(false);


                            console.log("[Adults][APPLY]", {
                              reservation_draft_id: selectedReservationId,
                              adults: num,
                            });
                          } catch (err) {
                            console.error("[Adults][ERROR_APPLY]", err);
                            alert(t("Error saving adults."));
                          }
                        }}
                      >
                        {num}
                      </div>
                    ))}

                    <div className="d-flex justify-content-end mt-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-light"
                        onClick={() => setIsEditingAdults(false)}
                      >
                        {t("Cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

    
    
    
              
    
             <div className="col-md-2 mb-3">
              <label className="form-label">{t("Children")}</label>
              <div
                className="form-control form-control-sm bg-light text-dark reservationPickerTrigger"
                style={{
                  cursor: formData.group_rate_id
                    ? "not-allowed"
                    : formData.check_in_date && formData.check_out_date
                    ? "pointer"
                    : "default",
                  opacity: formData.group_rate_id
                    ? 0.6
                    : formData.check_in_date && formData.check_out_date
                    ? 1
                    : 0.6,
                  minHeight: "42px", // 👈 más alto que antes
                  display: "flex",
                  alignItems: "center"
                }}
                onClick={async () => {
                if (formData.group_rate_id) {
                  alert(
                    t("Reservations with Group Rate cannot be modified. Please delete and create a new reservation.")
                  );
                  return;
                }

                if (!formData.check_in_date || !formData.check_out_date) return;

                try {
                  const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}/valid-children`
                  );

                  setAvailableChildren(res.data.valid_children || []);
                  setIsEditingChildren(true);

                } catch (err) {
                  console.error("[Children][ERROR_LOADING_OPTIONS]", err);
                  alert(t("Error loading children options."));
                }
              }}

              >
                <span
                  style={
                    childrenMap[selectedReservationId] > 0
                      ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" } // 👶 ≥1 → bold + más grande
                      : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" } // 👶 0 → muted + pequeño
                  }
                >
                  {childrenMap[selectedReservationId] || 0}
                </span>
              </div>


    
                {isEditingChildren && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                      zIndex: 1050,
                      backdropFilter: "blur(6px)",
                      backgroundColor: "rgba(23, 38, 83, 0.42)"
                    }}
                  >
                    <div
                      className="rounded p-4 shadow-lg"
                      style={{
                        minWidth: 250,
                        maxHeight: "80vh",
                        overflowY: "auto",
                        backgroundColor: "#ffffff",
                        color: "#26354f"
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">{t("Select Children")}</h5>
                        <button type="button" className="btn-close" aria-label={t("Close")} onClick={() => setIsEditingChildren(false)}></button>
                      </div>
    
                      {availableChildren.length === 0 && (
                        <div className="text-muted text-center">
                          {t("No options available")}
                        </div>
                      )}

                      {availableChildren.map(num => (
                        <div
                          key={num}
                          className="mb-2 p-2 border rounded text-center"
                          style={{ cursor: "pointer", backgroundColor: "#ffffff" }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#edf8fc"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#ffffff"}
                          onClick={async () => {
                          try {
                            // 1️⃣ Persistir en BD (la cascada YA se aplica en backend)
                            await axios.patch(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`,
                              {
                                children: num,
                                updated_by: "SelectChildren"
                              }
                            );

                            // 2️⃣ Volver a leer desde BD y pintar SOLO lo que hay allí
                            const { data } = await axios.get(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                            );

                            // 🔥 BD → UI (sin mezclar con estado previo)
                            setFormData({
                              ...data
                            });

                            // 🔥 Sincronizar mapas visuales con BD
                            setAdultsMap(prev => ({
                              ...prev,
                              [selectedReservationId]: data.adults
                            }));

                            setChildrenMap(prev => ({
                              ...prev,
                              [selectedReservationId]: data.children
                            }));

                            setIsEditingChildren(false);


                          } catch (err) {
                            console.error("[Children][ERROR_APPLY]", err);
                            alert(t("Error saving children."));
                          }
                        }}

                        >
                          {num}
                        </div>
                      ))}
    
                      <div className="d-flex justify-content-end mt-3">
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={() => setIsEditingChildren(false)}>
                          {t("Cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
    
    
              <div className="col-md-2 mb-3">
                <label className="form-label">{t("Babies")}</label>
                <input
                  key={`${selectedReservationId}-${formData.babies ?? "null"}`}  // fuerza repaint cuando cambia BD
                  type="number"
                  name="babies"
                  defaultValue={formData.babies ?? 0}                           // DB → UI (sin estado espejo)
                  min="0"
                  onBlur={async (e) => {
                    if (!formData.check_in_date || !formData.check_out_date) return;

                    const raw = e.target.value;
                    const babies = raw === "" ? 0 : Math.max(0, parseInt(raw, 10) || 0);

                    await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`, {
                      babies,
                      updated_by: "SelectBabies"
                    });

                    const { data: freshDraft } = await axios.get(
                      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                    );

                    setFormData({ ...freshDraft }); // pinta SOLO BD
                  }}
                  className="form-control form-control-sm p-1"
                  style={{
                    minHeight: "42px",
                    fontSize: (formData.babies ?? 0) > 0 ? "0.95rem" : "0.875rem",
                    fontWeight: (formData.babies ?? 0) > 0 ? "600" : "400",
                    color: (formData.babies ?? 0) > 0 ? "#212529" : "#6c757d",
                    opacity: formData.check_in_date && formData.check_out_date ? 1 : 0.6
                  }}
                  disabled={!formData.check_in_date || !formData.check_out_date}
                />

              </div>

    
              
             <div className="col-md-6 mb-3">
              <label className="form-label">{t("Room Category")}</label>
              
    
              <>
           <div
            className="mt-2 small text-muted border rounded p-2 bg-light text-dark d-flex justify-content-between align-items-center reservationPickerTrigger"
            style={{
              cursor: formData.group_rate_id
                ? "not-allowed"
                : formData.adults !== null
                ? "pointer"
                : "default",
              opacity: formData.group_rate_id
                ? 0.6
                : formData.adults !== null
                ? 1
                : 0.6
            }}
            onClick={async () => {
              if (formData.group_rate_id) return;
              if (formData.adults === null) return;

              try {
                // 🔥 0️⃣ Si hay overrides → eliminarlos ANTES de abrir selector
                if (formData.has_override) {
                  await axios.delete(
                    `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}/overrides`
                  );

                  // 🔁 Refetch desde BD (fuente de verdad)
                  const { data: refreshedDraft } = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                  );

                  setFormData(prev => ({
                    ...prev,
                    ...refreshedDraft
                  }));
                }

                // 🔎 Leer categorías válidas desde BD
                const res = await axios.get(
                  `/api/reservation-drafts/${selectedReservationId}/valid-room-categories`
                );

                setAvailableRoomCategories(res.data.categories || []);
                setIsEditingRoomCategory(true);

                console.log("[RoomCategory][OPEN_BY_DB_ADULTS]", {
                  reservation_draft_id: selectedReservationId,
                  adults: formData.adults
                });

              } catch (err) {
                console.error("[RoomCategory][ERROR]", err);
                alert(t("Error loading room categories."));
              }
            }}

          >
          <span
            style={
              formData.room_category_id
                ? { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                : {}
            }
          >
            {formData.room_category_id
              ? availableRoomCategories.find(
                  c => Number(c.id) === Number(formData.room_category_id)
                )?.name || t("Click to Select Category")
              : t("Click to Select Category")}
          </span>


          </div>


    
    
            {isEditingRoomCategory && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  zIndex: 1050,
                  backdropFilter: "blur(6px)",
                  backgroundColor: "rgba(23, 38, 83, 0.42)"
                }}
              >
                <div
                  className="rounded p-4 shadow-lg"
                  style={{
                    minWidth: 350,
                    maxHeight: "80vh",
                    overflowY: "auto",
                    backgroundColor: "#ffffff",
                    color: "#26354f"
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="m-0">{t("Select Category")}</h5>
                    <button type="button" className="btn-close" aria-label={t("Close")} onClick={() => setIsEditingRoomCategory(false)}></button>
                  </div>
    
                  {availableRoomCategories
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((cat) => (

                      <div
                        key={cat.id}
                        className="mb-2 p-2 border rounded"
                        style={{ cursor: "pointer", backgroundColor: "#ffffff" }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#edf8fc"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#ffffff"}
                        onClick={async () => {
                        try {
                          // 🔥 PATCH real al draft (fuente de verdad)
                          await axios.patch(
                            `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`,
                            {
                              room_category_id: cat.id,
                              room_id: null,
                              rate_id: null,
                              group_rate_id: null,
                              total_price: null,
                              night_price: null,
                              updated_by: "SelectRoomCategory"
                            }
                          );

                          // ✅ Read back from DB (source of truth) and render exactly that
                          const { data: freshDraft } = await axios.get(
                            `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                          );

                          setFormData((prev) => ({
                            ...prev,
                            ...freshDraft
                          }));

                          setIsEditingRoomCategory(false);

                          console.log("[RoomCategory][APPLY][DB_REFRESH]", {
                            reservation_draft_id: selectedReservationId,
                            patched_room_category_id: cat.id,
                            db_room_category_id: freshDraft?.room_category_id
                          });

                        } catch (err) {
                          console.error("[RoomCategory][ERROR_APPLY]", err);
                          alert(t("Error saving room category."));
                        }
                      }}

    
                      >    

                        <strong>{cat.name}</strong>
                      </div>
                  ))}
    
    
                  <div className="d-flex justify-content-end mt-3">
                    <button type="button" className="btn btn-sm btn-outline-light" onClick={() => setIsEditingRoomCategory(false)}>
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
    
    
    
    
    
    
            </div>
    
              
    
    
    
              <div className="col-md-6 mb-3 position-relative">
                <label className="form-label">{t("Room")}</label>

                {formData.room_category_id ? (
                  <>
                    <div
                      className="mt-2 small text-muted border rounded p-2 bg-light text-dark d-flex justify-content-between align-items-center reservationPickerTrigger"
                      style={{
                        cursor: formData.room_category_id ? "pointer" : "default",
                        opacity: formData.room_category_id ? 1 : 0.6
                      }}
                      onClick={async () => {
                      if (formData.room_category_id) {
                        try {
                          console.log("[Rooms][UI] Refetch before opening modal", {
                            reservationId: selectedReservationId,
                            categoryId: formData.room_category_id,
                            checkIn: formData.check_in_date,
                            checkOut: formData.check_out_date,
                          });

                          // 🔎 Pide al backend las habitaciones disponibles
                          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/available`, {
                            params: {
                              business_unit_id: formData.business_unit_id,
                              room_category_id: formData.room_category_id,
                              check_in_date: formData.check_in_date,
                              check_out_date: formData.check_out_date,
                              exclude_reservation_id: selectedReservationId,
                            },
                          });

                          // ⚡️ Actualiza la lista de rooms con lo devuelto
                          setRoomIdMap((prev) => ({
                            ...prev,
                            [selectedReservationId]: null, // limpiamos selección previa
                          }));

                          // 🚨 Aquí depende de cómo llenes `rooms`. Si lo recibes por props,
                          // tendrás que usar un estado local (roomsState) para guardar res.data.
                          // Si ya tienes setRooms en este componente, usa setRooms(res.data).
                          // Ejemplo con estado local:
                          setRoomsState(res.data || []);

                          console.log("[Rooms][UI] Rooms fetched:", res.data);
                        } catch (err) {
                          console.error("[Rooms][UI][error] fetching rooms:", err);
                          alert(t("Error fetching available rooms."));
                          return;
                        }

                        setIsEditingRoom(true);
                      }
                    }}

                    >
                      <span
                        style={
                          formData.room_id
                            ? { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                            : {}
                        }
                      >
                        {formData.room_id
                          ? roomIdMap?.[selectedReservationId]?.label || t("Room selected")
                          : t("Click to Select Room Number")}

                      </span>

                    </div>


                    {isEditingRoom && (
                      <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                          zIndex: 1050,
                          backdropFilter: "blur(6px)",
                          backgroundColor: "rgba(23, 38, 83, 0.42)"
                        }}
                      >
                        <div
                          className="rounded p-4 shadow-lg"
                          style={{
                            minWidth: 350,
                            maxHeight: "80vh",
                            overflowY: "auto",
                            backgroundColor: "#ffffff",
                            color: "#26354f"
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="m-0">{t("Select Room")}</h5>
                            <button
                              type="button"
                              className="btn-close" aria-label={t("Close")}
                              onClick={() => setIsEditingRoom(false)}
                            ></button>
                          </div>

                          {rooms.length === 0 && (
                            <div className="text-center text-muted">{t("No rooms available.")}</div>
                          )}

                          

                          {(() => {
                            console.log("🔎 Rooms recibidas para pintar:", rooms);
                            return [...rooms]
                              .sort((a, b) => {
                                const numA = parseInt(a.room_number);
                                const numB = parseInt(b.room_number);
                                return isNaN(numA) || isNaN(numB) ? 0 : numA - numB;
                              })
                              .map((room) => (
                                <div
                                  key={room.id}
                                  className="mb-2 p-2 border rounded"
                                  style={{
                                    cursor: "pointer",
                                    backgroundColor: "#ffffff"
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#edf8fc")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#ffffff")
                                  }
                                  onClick={async () => {
                                    setIsEditingRoom(false);

                                    try {
                                      // 1️⃣ PATCH real al draft (fuente de verdad)
                                      await axios.patch(
                                        `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`,
                                        {
                                          room_id: room.id,
                                          updated_by: "SelectRoom"
                                        }
                                      );

                                      // 2️⃣ Leer de BD y pintar exactamente lo que haya
                                      const { data: freshDraft } = await axios.get(
                                        `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                                      );

                                      setFormData((prev) => ({
                                        ...prev,
                                        ...freshDraft
                                      }));

                                      setRoomIdMap(prev => ({
                                        ...prev,
                                        [selectedReservationId]: {
                                          id: room.id,
                                          label: `${room.room_number}${room.name ? " – " + room.name : ""}`
                                        }
                                      }));


                                      console.log("[Room][APPLY][DRAFT_OK]", {
                                        reservation_draft_id: selectedReservationId,
                                        room_id: freshDraft.room_id
                                      });

                                    } catch (err) {
                                      console.error("[Room][ERROR_APPLY]", err);
                                      alert(t("Error saving room."));
                                    }
                                  }}

                                >
                                  <strong>{t("Room")} {room.room_number || "-"}</strong>
                                  <div
                                    className="mt-1"
                                    style={{
                                      color: "#5b6680",
                                      fontSize: "0.82rem",
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {getRoomMetadataLines(room).map((line) => (
                                      <div
                                        key={line.label}
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <span style={{ color: "#172653", fontWeight: 700 }}>
                                          {t(line.label)}:
                                        </span>{" "}
                                        {line.value}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ));
                          })()}

                          <div className="d-flex justify-content-end mt-3">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-light"
                              onClick={() => setIsEditingRoom(false)}
                            >
                              {t("Cancel")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="mt-2 small text-muted border rounded p-2 bg-light text-dark reservationPickerTrigger reservationPickerTrigger--disabled"
                    style={{ opacity: 0.6 }}
                  >
                    {t("Select Room Category First")}
                  </div>
                )}
              </div>

    
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("Room Rate")}</label>
    
                <div
                  className="mt-2 small text-muted border rounded p-2 bg-light text-dark d-flex justify-content-between align-items-center reservationPickerTrigger"
                  style={{ cursor: formData.room_category_id ? "pointer" : "default", opacity: formData.room_category_id ? 1 : 0.6 }}
                  onClick={async () => {
                    if (!formData.room_category_id) return;

                    if (!formData.check_in_date || !formData.check_out_date) {
                      alert(t("Select dates first."));
                      return;
                    }

                    if (formData.adults == null || formData.adults === "") {
                      alert(t("Select number of adults first."));
                      return;
                    }

                    try {
                      // 🔥 0️⃣ Si hay overrides → eliminarlos ANTES de abrir selector
                      if (formData.has_override) {
                        await axios.delete(
                          `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}/overrides`
                        );

                        // 🔁 Refetch desde BD (fuente de verdad)
                        const { data: refreshedDraft } = await axios.get(
                          `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                        );

                        setFormData(prev => ({
                          ...prev,
                          ...refreshedDraft
                        }));
                      }

                      console.log("[Rates][Draft][UI] fetching valid room rates", {
                        reservation_draft_id: selectedReservationId,
                        room_category_id: formData.room_category_id,
                        check_in_date: formData.check_in_date,
                        check_out_date: formData.check_out_date,
                        adults: formData.adults,
                        children: formData.children
                      });

                      // 🔄 limpiar lista previa
                      if (typeof setRoomRates === "function") setRoomRates([]);

                      const { data } = await axios.get(
                        `/api/reservation-drafts/${selectedReservationId}/valid-room-rates`
                      );

                      const rates = Array.isArray(data?.room_rates) ? data.room_rates : [];

                      if (typeof setRoomRates === "function") setRoomRates(rates);

                      console.log("[Rates][Draft][UI] rates loaded", {
                        reservation_draft_id: selectedReservationId,
                        total_candidates: data?.total_candidates,
                        total_valid: data?.total_valid,
                        rendered: rates.length
                      });

                      setIsEditingRate(true);

                    } catch (err) {
                      console.error("[Rates][Draft][UI][ERROR]", err);
                      if (typeof setRoomRates === "function") setRoomRates([]);
                      alert(t("Error loading room rates."));
                    }
                  }}

                >

                  <span
                    style={
                      formData.rate_id
                        ? { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                        : {}
                    }
                  >
                    {formData.rate_id
                      ? roomRates.find(r => Number(r.id) === Number(formData.rate_id))?.name
                        || t("Click to Select Room Rate")
                      : t("Click to Select Room Rate")}
                  </span>

                </div>

    
                {isEditingRate && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                      zIndex: 1050,
                      backdropFilter: "blur(6px)",
                      backgroundColor: "rgba(23, 38, 83, 0.42)"
                    }}
                  >
                    <div
                      className="rounded p-4 shadow-lg"
                      style={{
                        minWidth: 350,
                        maxHeight: "80vh",
                        overflowY: "auto",
                        backgroundColor: "#ffffff",
                        color: "#26354f"
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">{t("Select Room Rate")}</h5>
                        <button type="button" className="btn-close" aria-label={t("Close")} onClick={() => setIsEditingRate(false)}></button>
                      </div>
    
                      {roomRates.length === 0 && (
                        <div className="text-center text-muted">{t("No room rates available.")}</div>
                      )}
    
                      {roomRates.map(rate => (
                      <div
                        key={rate.id}
                        className="mb-2 p-2 border rounded"
                        style={{ cursor: "pointer", backgroundColor: "#ffffff" }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#edf8fc"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#ffffff"}
                        onClick={async () => {
                          console.log("[CLICK][RoomRate]", {
                            reservationId: selectedReservationId,
                            room_category_id: formData.room_category_id,
                            room_id: formData.room_id,
                            rate_id: formData.rate_id
                          });
                          setIsEditingRate(false);
                          const selectedRateId = rate.id;

                          try {
                            // 1️⃣ Persistir selección en BD (fuente de verdad)
                            await axios.patch(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`,
                              {
                                rate_id: selectedRateId,
                                group_rate_id: null,
                                updated_by: "SelectRoomRate"
                              }
                            );

                            // 2️⃣ Volver a leer desde BD y pintar
                            const { data } = await axios.get(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                            );

                            setFormData(prev => ({
                              ...prev,
                              ...data
                            }));

                            // 3️⃣ Recargar detalles de la tarifa desde BD
                            if (data.rate_id) {
                              const { data: rateDetails } = await axios.get(
                                `${process.env.REACT_APP_API_URL}/api/roomRates/${data.rate_id}`
                              );

                              setRoomRateDetailsMap(prev => ({
                                ...prev,
                                [selectedReservationId]: rateDetails
                              }));
                            }

                            // 🆕 4️⃣ Generar nightly prices DRAFT
                            await axios.post(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}/save-nightly-prices`,
                              {
                                room_rate_id: data.rate_id,
                                check_in_date: data.check_in_date,
                                check_out_date: data.check_out_date
                              }
                            );

                            // 🆕 5️⃣ Calcular totales desde nightly prices DRAFT
                            const { data: totals } = await axios.post(
                              `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}/totals/sync-from-draft-nightly-prices`
                            );

                            // 🆕 6️⃣ Pintar SOLO lo que devuelve la BD
                            setFormData(prev => ({
                              ...prev,
                              total_price: totals.total_price,
                              night_price: totals.night_price
                            }));

                            console.log("[Rate][Apply][DB_OK]", {
                              reservation_draft_id: selectedReservationId,
                              rate_id: selectedRateId
                            });

                          } catch (err) {
                            console.error("[Rate][Apply][ERROR]", err);
                            alert(t("Error applying room rate."));
                          }
                        }}
                      >
                        <strong>{rate.name}</strong>
                        {rate.client?.name && (
                          <span className="ms-2 text-secondary">({rate.client.name})</span>
                        )}
                      </div>
                    ))}

                      <div className="d-flex justify-content-end mt-3">
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={() => setIsEditingRate(false)}>
                          {t("Cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
    
             
    
              <div className="col-md-3 mb-3">
                <label className="form-label">{t("Room Price")} ({displayCurrency})</label>
                <div
                  className="mt-2 border rounded p-2 bg-light text-dark reservationValueField"
                  role="button"
                  onClick={openPriceModal}
                  title={t("See Nightly Breakdown")}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    style={
                      formData.total_price === 0
                        ? { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" } // 🔹 cero → muted + pequeño
                        : formData.total_price
                        ? { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" } // 🔹 >0 → bold + grande
                        : { color: "#6c757d" } // 🔹 null/undefined → pending (muted)
                    }
                  >
                    {formData.total_price !== null && formData.total_price !== undefined
                      ? formatFromBase(formData.total_price)
                      : t("Pending")}
                  </span>
                </div>
              </div>




             <div className="col-md-3 mb-3 d-flex align-items-end">
              {currentUser?.permissions?.["reservations.override_rates"] && (() => {
                const hasOverride = Boolean(formData?.has_override);


                console.log("[Overrides][UI][Render] has_override=%s | rate_id=%s", hasOverride, formData?.rate_id);

                const titleText = hasOverride
                  ? (formData?.rate_id
                      ? t("Remove overrides and restore base nightly prices")
                      : t("Select a Room Rate first"))
                  : (formData?.rate_id
                      ? t("Apply overrides to nightly prices")
                      : t("Select a Room Rate first"));

                const labelText = hasOverride ? t("Remove Override") : t("Apply Override");

                // Outline style like your Manage Extras example, but green (apply) / red (remove)
                const outlineClass = hasOverride ? "btn-outline-danger" : "btn-outline-success";

                return (
                  <button
                    type="button"
                    className={`btn ${outlineClass} w-100 fw-bold d-flex align-items-center justify-content-center mt-2`}
                    style={{ height: "42px" }}
                    disabled={!formData?.rate_id}
                    title={titleText}
                    onClick={async () => {
                      if (!formData?.rate_id) {
                        console.warn("[Overrides][UI] blocked: missing rate_id");
                        return;
                      }

                      if (hasOverride) {
                        try {
                          const resId = formData?.id ?? selectedReservationId;

                          await axios.delete(`/api/reservation-drafts/${resId}/overrides`);

                          await refetchReservationAfterReset({
                            reservationId: resId,
                            setFormData,
                            setCustomerData,
                            setGuestCustomers,
                            setGuestChildren,
                          });
                        } catch (err) {
                          console.error("[Overrides][UI] Remove failed:", err);
                        }
                      }
                      else {
                        // APPLY OVERRIDE
                        setIsOverridePopupOpen(true);
                        console.log("[Overrides][UI] open modal (Apply)");
                      }
                    }}
                  >
                    {labelText}
                  </button>
                );
              })()}
            </div>




          
            {/* Extras details */}
            <div className="col-md-5">
              <label className="form-label">{t("Extras details")}</label>
              <input
                type="text"
                readOnly
                value={(() => {
                  console.log("🔎 [Extras][Render] formData.extras:", formData.extras);
                  console.log("🔎 [Extras][Render] formData.extras_details:", formData.extras_details);

                  const value = formData.extras_details?.length
                    ? formData.extras_details.map((ex) => ex.name || t("Unnamed Extra")).join(", ")
                    : formData.extras?.length
                      ? formData.extras.map((ex) => ex.name || t("Unnamed Extra")).join(", ")
                      : t("No extras");

                  console.log("🔎 [Extras][Render] value final para input:", value);
                  return value;
                })()}
                className="form-control bg-secondary text-light fw-bold mt-2 reservationExtrasField"
                style={{ height: "42px", fontSize: "0.95rem", lineHeight: "1.2" }}
                onClick={() => setExtrasModalMode("details")}
              />

            </div>

            {/* Manage extras */}
            <div className="col-md-2">
              <label className="form-label">&nbsp;</label>
              {currentUser?.permissions?.["reservations.manage_extras"] && (
                <button
                  type="button"
                  className="btn btn-outline-primary w-100 fw-bold d-flex align-items-center justify-content-center mt-2"
                  style={{ height: "42px" }}
                  onClick={() => setExtrasModalMode("manage")}
                  disabled={!formData.rate_id || !formData.total_price}
                  title={
                    !formData.rate_id || !formData.total_price
                      ? t("Select a Room Rate first to manage extras")
                      : t("Manage Extras")
                  }
                >
                  {t("Manage Extras")}
                </button>
              )}
            </div>


            {/* Total Extras */}
            <div className="col-md-2">
              <label className="form-label">{t("Total Extras")} ({displayCurrency})</label>
              <div
                className="border rounded p-2 bg-light text-dark d-flex align-items-center mt-2 reservationValueField"
                style={{ height: "42px" }}
                title={t("Total applied extras")}
              >
                <span
                  style={
                    Number(formData.total_extras || 0) === 0
                      ? { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                      : { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                  }
                >
                  {formatFromBase(formData.total_extras || 0)}
                </span>
              </div>
            </div>

            {/* Final Total Price */}
            <div className="col-md-3">
              <label className="form-label">{t("Final Total Price")} ({displayCurrency})</label>
              <div
                className="border rounded p-2 bg-light text-dark d-flex align-items-center mt-2 reservationValueField"
                style={{ height: "42px" }}
                title={t("Reservation total including extras")}
              >
                {formData.total_price != null && (
                  <span
                    style={
                      Number(formData.total_price) + Number(formData.total_extras || 0) === 0
                        ? { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                        : { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                    }
                  >
                    {formatFromBase(
                      Number(formData.total_price) + Number(formData.total_extras || 0)
                    )}
                  </span>
                )}

              </div>
            </div>
              <div className="row mt-3"></div>
              <div className="col-md-2 mb-3">
                <label className="form-label">{t("Check-in Time")}</label>
                <input
                  key={`${selectedReservationId}-in-${formData.check_in_time ?? "null"}`}
                  type="time"
                  name="check_in_time"
                  defaultValue={toHHMM(formData.check_in_time)}
                  onBlur={async (e) => {
                    const check_in_time = toDbTime(e.target.value);

                    await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`, {
                      check_in_time,
                      updated_by: "SelectCheckInTime"
                    });

                    const { data: freshDraft } = await axios.get(
                      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                    );

                    setFormData((prev) => ({
                      ...prev,
                      ...freshDraft,
                      extras: freshDraft.extras ?? prev.extras,
                      extras_details: freshDraft.extras_details ?? prev.extras_details,
                    }));

                  }}
                  className="form-control form-control-sm p-1"
                />
    
              </div>
    
              
              <div className="col-md-2 mb-3">
                <label className="form-label">{t("Check-out Time")}</label>
                <input
                  key={`${selectedReservationId}-out-${formData.check_out_time ?? "null"}`}
                  type="time"
                  name="check_out_time"
                  defaultValue={toHHMM(formData.check_out_time)}
                  onBlur={async (e) => {
                    const check_out_time = toDbTime(e.target.value);

                    await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`, {
                      check_out_time,
                      updated_by: "SelectCheckOutTime"
                    });

                    const { data: freshDraft } = await axios.get(
                      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                    );

                    setFormData((prev) => ({
                      ...prev,
                      ...freshDraft,
                      extras: freshDraft.extras ?? prev.extras,
                      extras_details: freshDraft.extras_details ?? prev.extras_details,
                    }));

                  }}
                  className="form-control form-control-sm p-1"
                />
              </div>

    
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("Special Requests")}</label>
                <textarea
                  key={`${selectedReservationId}-${formData.special_requests ?? "null"}`}  // Forzar repaint cuando cambia BD
                  name="special_requests"
                  defaultValue={formData.special_requests ?? ""} // Lee directamente desde la base de datos
                  onBlur={async (e) => {
                    const raw = e.target.value;
                    const specialRequests = raw.trim();

                    await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`, {
                      special_requests: specialRequests,
                      updated_by: "AutoSaveSpecialRequests"
                    });

                    const { data: freshDraft } = await axios.get(
                      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
                    );

                    setFormData((prev) => ({
                      ...prev,
                      ...freshDraft,
                      extras: freshDraft.extras ?? prev.extras,
                      extras_details: freshDraft.extras_details ?? prev.extras_details,
                    }));
                  }}

                  className="form-control form-control-sm p-1"
                  rows="1"
                  style={{ resize: "none" }}
                />

              </div>
    
              <div className="col-md-2 mb-3">
                <label className="form-label">{t("Reservation Status")}</label>
                <input
                  type="text"
                  value={translateReservationStatus(formData.status, languageId)}
                  readOnly
                  className="form-control form-control-sm p-1 bg-secondary text-light fw-bold text-center reservationStatusField"
                />
              </div>

              <ReservationExtrasModal
                show={extrasModalMode === "manage"}
                onClose={() => setExtrasModalMode(null)}
                reservationId={selectedReservationId}
                businessUnitId={formData.business_unit_id}
                onSaved={({ totals, rows }) => {
                  console.log("[ReservationForm][Extras][onSaved] Callback ejecutado");
                  console.log("   → Totals recibidos:", totals);
                  console.log("   → Rows recibidas:", rows);

                  // 🔹 Mapeamos detalles con fallback al nombre correcto
                  const mappedDetails = rows.map((r) => {
                    const name = r.extra?.name || r.name || "";
                    console.log("   → Procesando row:", r, " → name usado:", name);
                    return {
                      id: r.id,
                      name,
                      amount: r.calculated_amount,
                    };
                  });

                  console.log("   → mappedDetails final:", mappedDetails);

                  // ✅ Actualizar formData con los extras y totales
                  setFormData((prev) => {
                    const updated = {
                      ...prev,
                      extras: rows,
                      extras_details: mappedDetails,
                      total_extras: totals?.extras || 0,
                    };
                    console.log("   ✅ [Extras][setFormData] Actualizado:", updated);
                    return updated;
                  });
                }}
              />









    
    
            </div>
            <OverridePopup
              reservationId={selectedReservationId}
              baseCurrency={baseCurrency}
              isOpen={isOverridePopupOpen}
              onClose={() => setIsOverridePopupOpen(false)}
              onApplied={async (data) => {
                console.log("[Overrides][UI][Applied] override saved, resId=", selectedReservationId);

                // 1) Optimistic update → fuerza la UI a rojo inmediatamente
                try {
                  if (typeof setHasOverrideFlag === "function") {
                    setHasOverrideFlag(true);
                    console.log("[Overrides][UI] hasOverrideFlag => true (optimistic)");
                  }
                } catch (e) {
                  console.warn("[Overrides][UI] setHasOverrideFlag not available?", e?.message);
                }

                // 2) Refresca TODOS los datos desde BD (fuente de verdad)
                await refetchReservationAfterReset({
                  reservationId: selectedReservationId,
                  setFormData,
                  setCustomerData,
                  setGuestCustomers,
                  setGuestChildren,
                });

                // 3) (Opcional robustez) Re-sincroniza flag con GET puntual
                try {
                  const { data: resRow } = await axios.get(`/api/reservations/${selectedReservationId}`);
                  const realFlag = !!resRow?.has_override;
                  if (typeof setHasOverrideFlag === "function") {
                    setHasOverrideFlag(realFlag);
                    console.log("[Overrides][UI] synced has_override from DB =", realFlag);
                  }
                } catch (err) {
                  console.error("[Overrides][UI] failed to re-sync has_override:", err?.message);
                }
              }}
            />





    
           
            </div>
    
            {/* 🔘 Botones compactos, adaptables, y con texto dinámico */}
            <div className="reservationFormActions" style={{
              width: "100%",
              display: "flex",
              gap: "8px",
              padding: "0 1rem",
              marginTop: "1rem"
            }}>
              <button
                type="submit"
                className="btn text-white fw-bold border-0 reservationFormSave"
                style={{
                  flex: 1,
                  background: "#223a7a",
                  borderRadius: "6px",
                  padding: "6px 0",
                  fontSize: "0.75rem"
                }}
              >
                {groupReservations.length > 1 ? t("Save All Rooms") : t("Save Reservation")}
              </button>
    
              {!!reservationIdRef.current && (
                <button
                  type="button"
                  onClick={handleDeleteReservation}
                  className="btn text-white fw-bold border-0 reservationFormDelete"
                  style={{
                    flex: 1,
                    background: "#9b3445",
                    borderRadius: "6px",
                    padding: "6px 0",
                    fontSize: "0.75rem"
                  }}
                >
                  {groupReservations.length > 1 ? t("Delete All Rooms") : t("Delete Reservation")}
                </button>
              )}
            </div>

          </div>
      </form>

      {showPriceModal && (
        <>
          <div
            className="modal fade show reservationPriceModal"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t("Nightly Breakdown")}</h5>
                  <button type="button" className="btn-close" aria-label={t("Close")} onClick={closePriceModal}></button>
                </div>
                <div className="modal-body">
                  {!canLoadBreakdown && (
                    <div className="text-muted">{t("Choose dates and rates to see the breakdown.")}</div>
                  )}
                  {canLoadBreakdown && loadingBreakdown && (
                    <div className="text-muted">{t("Loading…")}</div>
                  )}
                  {canLoadBreakdown && !loadingBreakdown && breakdownError && (
                    <div className="text-danger">{t(breakdownError)}</div>
                  )}
                  {canLoadBreakdown && !loadingBreakdown && !breakdownError && (
                    <div>
                      {nightly.map(n => (
                        <div key={n.date}>
                          {new Date(n.date + "T00:00:00").toLocaleDateString(getReservationLocale(languageId))} — {formatFromBase(n.price)}
                        </div>
                      ))}
                      <div className="fw-bold mt-2">
                        {t("Total")}: {formatFromBase(nightly.reduce((s, n) => s + Number(n.price || 0), 0))}
                      </div>
                    </div>
                  )}

                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={closePriceModal}>
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* backdrop */}
          <div className="modal-backdrop fade show" onClick={closePriceModal}></div>
        </>
      )}

      {isSaving && (
        <div
          className="reservationSavingOverlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(23,38,83,0.78)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            fontWeight: "bold",
            zIndex: 999999,
            pointerEvents: "all",
            cursor: "wait",
          }}
        >
          {t("Saving Reservation…")}
        </div>
      )}

    </div>
  )
  );
}

export default ReservationForm;
